/**
 * @typedef {Object} CustomerBuysCollection
 * @property {string} handle
 * @property {string} id
 * @property {number} productCount
 * @property {string} title
 */

/**
 * @typedef {Object} CustomerBuysProductVariant
 * @property {string} id
 * @property {string} price
 */

/**
 * @typedef {Object} CustomerBuysProduct
 * @property {string} handle
 * @property {string} id
 * @property {string} title
 * @property {CustomerBuysProductVariant[]} variants
 * @property {number} variantCount
 */

/**
 * @typedef {Object} CustomerBuysConfig
 * @property {string} amount
 * @property {CustomerBuysCollection[]} collections
 * @property {'MinimumPurchase' | 'MinimumQuantity'} discountType
 * @property {'Collection' | 'Product'} itemType
 * @property {CustomerBuysProduct[]} products
 * @property {string} quantity
 * @property {string} query
 */

class GiftWithPurchase {
  constructor () {
    this.onPushCartChange = this.onPushCartChange.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onPushCartChange,
    )
  }

  async onPushCartChange (event) {
    if (!this.shouldRun) return

    // Runs synchronously before CartItem.removeLineItem gets control back and calls renderSections.
    // If the threshold is no longer met, strip GWP cart items from the sections HTML right now
    // so the push cart never renders with them visible.
    const eventState = event.detail?.state
    if (eventState?.items && eventState?.sections) {
      const gwpKeys = eventState.items
        .filter((item) => item.properties?._gwp)
        .map((item) => item.key)

      if (gwpKeys.length && !this.getCartIsValid(eventState)) {
        this.removeGwpKeysFromSections(eventState.sections, gwpKeys)
      }
    }

    /**
     * @type {ShopifyCart | null}
     */
    const state =
      eventState != null && 'discount_codes' in eventState
        ? eventState
        : await window.BAO.Utils.fetchJSON(`/cart.js`)

    const cartIsValid = this.getCartIsValid(state)

    if (!cartIsValid) {
      await this.removeGwpItems(state)
    }

    if (this.applyDiscountCodeAutomatically) {
      const alreadyHasDiscountCode = this.getAlreadyHasDiscountCode(state)

      if (alreadyHasDiscountCode && !cartIsValid) {
        await this.removeDiscountCode(state)
      } else if (cartIsValid && !alreadyHasDiscountCode) {
        await this.addDiscountCode(state)
      }
    }

    // When a GWP item is first added, the sections from the add response don't yet
    // have the discount applied. Fetch fresh sections now (after any discount code
    // has been applied above) so the push cart shows the correct discounted price.
    if (eventState?.properties?._gwp && cartIsValid) {
      const sr = window.BAO.sectionRenderer
      const htmlSections = await sr.fetch(
        sr.pushCartSections.map((s) => s.section),
      )
      const sectionsToRender = sr.pushCartSections
        .filter((s) => !s.dontRender && s.section in htmlSections)
        .map((s) => ({ ...s, html: htmlSections[s.section] }))
      if (sectionsToRender.length) {
        sr.renderMultiple(sectionsToRender)
      }
    }
  }

  /**
   * @param {ShopifyCart} state
   */
  addDiscountCode (state) {
    return window.BAO.Cart.updateCartDiscount(
      [
        ...this.getCurrentAppliedDiscountCodes(state).filter(
          (code) => code !== this.discountCode,
        ),
        this.discountCode,
      ].join(','),
      { suppressDiscountValidation: true },
    )
  }

  /**
   * @param {ShopifyCart} state
   */
  removeDiscountCode (state) {
    return window.BAO.Cart.updateCartDiscount(
      [
        ...this.getCurrentAppliedDiscountCodes(state).filter(
          (code) => code !== this.discountCode,
        ),
      ].join(','),
      { suppressDiscountValidation: true },
    )
  }

  /**
   * Strips GWP cart item elements from Shopify sections HTML in-place, so the push cart
   * renders the correct state before the removal API call completes.
   *
   * @param {Object} sections - sections object from the PUSH_CART.CHANGED event state
   * @param {string[]} gwpKeys - cart item keys for GWP items to remove
   */
  removeGwpKeysFromSections (sections, gwpKeys) {
    for (const sectionKey of Object.keys(sections)) {
      const html = sections[sectionKey]
      if (!html) continue

      const dom = new DOMParser().parseFromString(html, 'text/html')
      let modified = false

      for (const key of gwpKeys) {
        const cartItemEl = dom.querySelector(`cart-item[data-key="${key}"]`)
        if (!cartItemEl) continue

        const wrapper = cartItemEl.closest('.psh-CartItems_Item');
        (wrapper || cartItemEl).remove()
        modified = true
      }

      if (modified) {
        sections[sectionKey] = dom.body.innerHTML
      }
    }
  }

  /**
   * @param {ShopifyCart} state
   */
  async removeGwpItems (state) {
    const gwpItems = state.items.filter((item) => item.properties?._gwp)

    if (gwpItems.length === 0) return

    let finalState
    for (const item of gwpItems) {
      finalState = await window.BAO.Cart.removeItem(item.key)
    }

    // Re-render push cart with the post-removal sections so the GWP block correctly
    // shows the threshold-not-met state (it was empty while the GWP item was still claimed).
    if (finalState?.sections) {
      const sr = window.BAO.sectionRenderer
      const sectionsToRender = sr.pushCartSections
        .filter((s) => s.section in finalState.sections)
        .map((s) => ({ ...s, html: finalState.sections[s.section] }))

      if (sectionsToRender.length) {
        sr.renderMultiple(sectionsToRender)
      }
    }
  }

  /**
   * @returns {string | null}
   */
  get discountCode () {
    return this.shouldRun ? this.data.discountCode : null
  }

  /**
   * @param {ShopifyCart} cart
   *
   * @returns {boolean}
   */
  getAlreadyHasDiscountCode (cart) {
    return cart.discount_codes.some(
      (discountCode) => discountCode.code === this.discountCode,
    )
  }

  /**
   * @returns {boolean}
   */
  get applyDiscountCodeAutomatically () {
    if (!this.shouldRun) return false

    return this.data.applyCodeAutomatically
  }

  /**
   * @param {ShopifyCart} cart
   *
   * @returns {boolean}
   */
  getCartIsValid (cart) {
    const {
      discountType,
      itemType,
      thresholdAmount,
      thresholdQuantity,
      qualifyingProductIds,
    } = this.data

    // Collection-based GWPs can't be evaluated from cart.js alone (no collection membership data)
    if (itemType === 'Collection' || !qualifyingProductIds?.length) {
      return this.data.passedThreshold
    }

    const qualifyingItems = cart.items.filter(
      (item) =>
        !item.properties?._gwp &&
        qualifyingProductIds.includes(item.product_id),
    )

    if (discountType === 'MinimumPurchase') {
      const total = qualifyingItems.reduce(
        (sum, item) => sum + item.final_line_price,
        0,
      )
      return total >= thresholdAmount
    }

    if (discountType === 'MinimumQuantity') {
      const qty = qualifyingItems.reduce((sum, item) => sum + item.quantity, 0)
      return qty >= thresholdQuantity
    }

    return this.data.passedThreshold
  }

  /**
   * @param {ShopifyCart} cart
   *
   * @returns {string[]}
   */
  getCurrentAppliedDiscountCodes (cart) {
    return cart.discount_codes.map((discountCode) => discountCode.code)
  }

  get dataElement () {
    return document.querySelector(`script[data-gift-with-purchase-data]`)
  }

  get shouldRun () {
    return !!this.dataElement
  }

  /**
   * @returns {{
   *   applyCodeAutomatically: boolean,
   *   discountCode: string,
   *   passedThreshold: boolean
   * }}
   */
  get data () {
    return JSON.parse(this.dataElement.textContent.trim())
  }
}

window.BAO.GiftWithPurchase = new GiftWithPurchase()

class GiftWithPurchaseItem extends window.BAO.CustomElement() {
  static requiredAttributes () {
    return ['data-product-handle']
  }

  static requiredElements () {
    return ['form', 'idInput', 'product-option', 'option-value-display']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'change',
      this.onFormChange.bind(this),
    )
  }

  async onFormChange (e) {
    const chosenOption = Array.from(e.target.options).find(
      (option) => option.value === e.target.value,
    )
    if (!chosenOption) {
      console.error(`Unable to find option for ${e.target.value}`)

      return
    }

    this.setIdInputValue(chosenOption.getAttribute('data-variant-id'))

    const selectIndex = this.els['product-option'].elements.indexOf(e.target)
    const valueDisplay = this.els['option-value-display'].elements[selectIndex]
    if (valueDisplay) valueDisplay.textContent = chosenOption.text

    if (this.allValuesAreFilled) {
      return this.els.form.element.requestSubmit()
    }

    const productOptionValues = this.els['product-option'].elements.map(
      (option) => {
        if (option.value === '') {
          return option.getAttribute('data-default-option-value-id')
        }

        const selectedOption = Array.from(option.options).find(
          (o) => o.value === option.value,
        )

        return selectedOption.getAttribute('data-option-id')
      },
    )

    const response = await window.BAO.sectionRenderer.fetchSingle(
      'product-gift-with-purchase-unclaimed',
      `/products/${this.getAttribute('data-product-handle')}?option_values=${productOptionValues.join(',')}`,
    )
    const newSection = new DOMParser().parseFromString(response, 'text/html')

    const newProductOptions = Array.from(
      newSection.querySelectorAll(this.els['product-option'].selector),
    )

    for (const newProductOption of newProductOptions) {
      const currentProductOption = this.els['product-option'].elements.find(
        (option) =>
          option.getAttribute('name') === newProductOption.getAttribute('name'),
      )
      if (!currentProductOption) continue

      for (let i = 0; i < currentProductOption.options.length; i++) {
        const currentOption = currentProductOption.options[i]
        const newOption = newProductOption.options[i]

        if (currentOption.value === '') continue

        currentOption.setAttribute(
          'data-variant-id',
          newOption.getAttribute('data-variant-id'),
        )

        if (!currentOption.hasAttribute('data-perma-disabled')) {
          currentOption.disabled = newOption.disabled
        }
      }
    }
  }

  /**
   * @param {string} value
   */
  setIdInputValue (value) {
    this.els.idInput.element.value = value
  }

  get allValuesAreFilled () {
    return this.els['product-option'].elements.every((option) => {
      return option.value !== ''
    })
  }
}

if (!customElements.get('gift-with-purchase-item')) {
  customElements.define('gift-with-purchase-item', GiftWithPurchaseItem)
}

class GiftWithPurchasePDP extends HTMLElement {
  connectedCallback () {
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartChange.bind(this),
    )
  }

  async onCartChange () {
    const sectionId = this.closest('product-info')?.dataset.sectionId
    if (!sectionId) return

    const response = await window.BAO.sectionRenderer.fetchSingle(
      sectionId,
      window.location.pathname,
    )

    const newDOM = window.BAO.sectionRenderer.convertToDOM(response)
    const newContent = newDOM.querySelector('gift-with-purchase-pdp')

    if (newContent) {
      this.innerHTML = newContent.innerHTML
    }
  }
}

if (!customElements.get('gift-with-purchase-pdp')) {
  customElements.define('gift-with-purchase-pdp', GiftWithPurchasePDP)
}
